import { supabase } from './supabase';

type Loose = Record<string, unknown>;
const obj = (v: unknown): Loose => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Loose) : {});
const str = (v: unknown) => (typeof v === 'string' ? v : '');

// The creator web portal's "How to use your coach" page (/help and the public
// /access-help), edited on the Coach Guide page. Stored as one JSON document
// in coach_help_guide (single row, id 'default').
//
// Keep the shape, limits, defaults and link syntax in sync with
// creator-app-web/src/services/coachGuide.ts — the portal repairs anything it
// doesn't recognise and falls back to its own built-in guide.

/**
 * A run of formatted text. Every text field on the page (title, subtitle, section label and text, prompts) is
 * arrays of runs; "\n" inside `text` is a line break. Older guides stored
 * these fields as strings with [label](url) links; toEditable converts them.
 */
export type RichRun = { text: string; bold?: boolean; italic?: boolean; href?: string; size?: number };
export type RichText = RichRun[];
export type GuidePrompt = { id: string; title: RichText; text: RichText };
export type SectionKind = 'prompts' | 'text';
export type ImagePosition = 'top' | 'bottom';
export type ImageWidth = 'full' | 'medium' | 'small';
export type GuideImage = { url: string; alt: string; position: ImagePosition; width: ImageWidth };
export type GuideSection = {
  id: string;
  kind: SectionKind;
  /** Section label, e.g. START HERE. */
  eyebrow: RichText;
  /** Intro line under the eyebrow (prompts) or the block's text (text). */
  body: RichText;
  /** Per-section override of theme.accent; null uses the theme's. */
  accent: string | null;
  image: GuideImage | null;
  items: GuidePrompt[];
};
export type GuideTheme = {
  pageTitle: string;
  pageSubtitle: string;
  accent: string;
  link: string;
  panelBackground: string;
  panelBorder: string;
  eyebrow: string;
  promptTitle: string;
  quoteBackground: string;
  quoteText: string;
  bodyText: string;
  buttonText: string;
};
/** Font sizes in px (desktop; the portal scales the title and subtitle down on small screens). */
export type GuideTypography = {
  titleSize: number;
  subtitleSize: number;
  eyebrowSize: number;
  bodySize: number;
  promptTitleSize: number;
  promptTextSize: number;
  buttonSize: number;
};
export type CoachGuide = {
  version: 1;
  title: RichText;
  subtitle: RichText;
  theme: GuideTheme;
  typography: GuideTypography;
  sections: GuideSection[];
};

export type CoachGuideRow = {
  content: CoachGuide;
  revision: number;
  updated_at: string;
  updated_by_email: string | null;
};

export const GUIDE_LIMITS = {
  title: 120,
  subtitle: 300,
  eyebrow: 60,
  body: 2000,
  promptTitle: 100,
  promptText: 2000,
  sections: 20,
  items: 30,
  alt: 200,
} as const;

/** Per-selection font sizes offered in the text editor's size menu (the portal clamps to 9–64). */
export const RICH_SIZE_RANGE = { min: 9, max: 64 } as const;
export const RICH_SIZES = [10, 11, 12, 13, 14, 15, 16, 18, 20, 24, 28, 32, 40];


export const DEFAULT_THEME: GuideTheme = {
  pageTitle: '#ffffff',
  pageSubtitle: '#9b9ba4',
  accent: '#ff70c4',
  link: '#ff70c4',
  panelBackground: '#0c0c10',
  panelBorder: '#ffffff16',
  eyebrow: '#a3a3ac',
  promptTitle: '#ffffff',
  quoteBackground: '#ffffff06',
  quoteText: '#c9c9d1',
  bodyText: '#9b9ba4',
  buttonText: '#eeeeee',
};

export const DEFAULT_TYPOGRAPHY: GuideTypography = {
  titleSize: 38,
  subtitleSize: 15,
  eyebrowSize: 11,
  bodySize: 13.5,
  promptTitleSize: 14,
  promptTextSize: 13.5,
  buttonSize: 13,
};

type FieldGroup = 'Page' | 'Sections' | 'Prompts';

/** Size fields in the order the editor shows them. The portal clamps to the same ranges. */
export const TYPOGRAPHY_FIELDS: { key: keyof GuideTypography; label: string; hint: string; min: number; max: number; group: FieldGroup }[] = [
  { key: 'titleSize', label: 'Page title', hint: 'Shrinks a little on phones', min: 24, max: 64, group: 'Page' },
  { key: 'subtitleSize', label: 'Subtitle', hint: 'The line under the title', min: 12, max: 24, group: 'Page' },
  { key: 'eyebrowSize', label: 'Section label', hint: 'e.g. START HERE', min: 9, max: 24, group: 'Sections' },
  { key: 'bodySize', label: 'Intro & text', hint: 'Section intros and text blocks', min: 11, max: 24, group: 'Sections' },
  { key: 'promptTitleSize', label: 'Prompt title', hint: 'e.g. Find products that fit me', min: 11, max: 28, group: 'Prompts' },
  { key: 'promptTextSize', label: 'Prompt text', hint: 'The copyable prompt', min: 11, max: 24, group: 'Prompts' },
  { key: 'buttonSize', label: 'Copy button', hint: 'Copy / Copied label', min: 10, max: 20, group: 'Prompts' },
];

/** Colour fields in the order the editor shows them. */
export const THEME_FIELDS: { key: keyof GuideTheme; label: string; hint: string; group: FieldGroup }[] = [
  { key: 'pageTitle', label: 'Page title', hint: 'The big heading', group: 'Page' },
  { key: 'pageSubtitle', label: 'Subtitle', hint: 'The line under the heading', group: 'Page' },
  { key: 'link', label: 'Links', hint: 'Clickable links in any text', group: 'Page' },
  { key: 'panelBackground', label: 'Card background', hint: 'Each section card', group: 'Sections' },
  { key: 'panelBorder', label: 'Card border & dividers', hint: 'Card outline, image frame, lines between prompts', group: 'Sections' },
  { key: 'eyebrow', label: 'Section label', hint: 'e.g. START HERE', group: 'Sections' },
  { key: 'bodyText', label: 'Body text', hint: 'Text blocks and section intros', group: 'Sections' },
  { key: 'accent', label: 'Accent', hint: 'Bar beside each prompt (sections can override)', group: 'Prompts' },
  { key: 'promptTitle', label: 'Prompt title', hint: 'e.g. Find products that fit me', group: 'Prompts' },
  { key: 'quoteBackground', label: 'Prompt background', hint: 'Box behind the prompt text', group: 'Prompts' },
  { key: 'quoteText', label: 'Prompt text', hint: 'The copyable prompt', group: 'Prompts' },
  { key: 'buttonText', label: 'Copy button', hint: 'Copy button label and icon', group: 'Prompts' },
];

const plainText = (text: string): RichText => [{ text }];
const prompt = (id: string, title: string, text: string): GuidePrompt => ({ id, title: plainText(title), text: plainText(text) });
const plain = { accent: null, image: null } as const;

// Mirrors the portal's built-in guide (creator-app-web components/coach/prompts.ts).
export const DEFAULT_GUIDE: CoachGuide = {
  version: 1,
  title: plainText('How to use your coach'),
  subtitle: [{ text: 'Open a chat in Claude with BMC Coach on, paste one of these, and build on the answer.' }],
  theme: DEFAULT_THEME,
  typography: DEFAULT_TYPOGRAPHY,
  sections: [
    {
      id: 'start', kind: 'prompts', eyebrow: plainText('START HERE'), body: [], ...plain,
      items: [
        prompt('start-1', 'Find products that fit me', 'Give me the top hidden gems that fit my niche and my audience. For each one, tell me why it fits me specifically, and include the product link.'),
        prompt('start-2', 'Write 3 scripts for a product', 'Write me 3 scripts for [product]. Use hooks that match my style and what’s converting right now.'),
        prompt('start-3', 'Plan this week from my showcase', 'Look at my TikTok showcase and tell me which products I should film this week, and why.'),
        prompt('start-4', 'Diagnose one of my videos', 'Here’s a video of mine: [link]. Why did it perform the way it did, and what one thing should I change?'),
        prompt('start-5', 'See what’s working in my niche', 'What’s working in [niche] right now? Show me the pattern and 3 videos to study.'),
      ],
    },
    {
      id: 'more', kind: 'prompts', eyebrow: plainText('MORE TO TRY'), body: [], ...plain,
      items: [
        prompt('more-1', '5 hooks for a product', 'Give me 5 opening hooks for [product]: the spoken line, the on-screen text, and the first shot.'),
        prompt('more-2', 'Make a script shorter', 'Cut this script to about 20 seconds without losing the hook: [script]'),
        prompt('more-3', 'Make it sound like me', 'Rewrite this in my voice: [script]'),
        prompt('more-4', 'Compare two of my videos', 'Compare [video 1] and [video 2]. What’s different about the hooks and structure, and which should I do more of?'),
        prompt('more-5', 'Weekly hidden gems by email', 'Email me 5 new hidden gems every Monday at 8am.'),
        prompt('more-6', 'Teach your coach something', 'Remember this about me: [detail]. Confirm what you saved.'),
      ],
    },
    {
      id: 'good-to-know', kind: 'text', eyebrow: plainText('GOOD TO KNOW'), ...plain, items: [],
      body: [{ text: 'Your coach lives inside Claude and works in the Claude phone app too. It knows your videos, your style, and today’s hidden gems. It can’t see your sales or commissions, and every script is a draft for you to make your own.' }],
    },
  ],
};

export const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export const newId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`).slice(0, 12);

export const emptyPrompt = (): GuidePrompt => ({ id: newId(), title: [], text: [] });
export const emptySection = (kind: SectionKind): GuideSection => ({
  id: newId(),
  kind,
  eyebrow: plainText(kind === 'prompts' ? 'NEW PROMPTS' : 'NEW SECTION'),
  body: [],
  ...plain,
  items: kind === 'prompts' ? [emptyPrompt()] : [],
});

// ─── Links ──────────────────────────────────────────────────────────────────
// Only http(s) and mailto become links. tokenizeLinks reads the older string
// format ([label](https://…) and bare https:// URLs) — same rules as the portal.

export type RichToken = { type: 'text'; value: string } | { type: 'link'; label: string; href: string };

export function safeHref(raw: string): string | null {
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' || url.protocol === 'http:' || url.protocol === 'mailto:' ? url.href : null;
  } catch {
    return null;
  }
}

const LINK_RE = /\[([^\]\n]+)\]\(([^)\s]+)\)|https?:\/\/[^\s<>()[\]]+/g;
const TRAILING_PUNCT = /[.,!?;:'"]+$/;

export function tokenizeLinks(text: string): RichToken[] {
  const out: RichToken[] = [];
  const pushText = (value: string) => {
    if (!value) return;
    const last = out[out.length - 1];
    if (last?.type === 'text') last.value += value;
    else out.push({ type: 'text', value });
  };
  let at = 0;
  for (const m of text.matchAll(LINK_RE)) {
    const start = m.index ?? 0;
    pushText(text.slice(at, start));
    if (m[1] !== undefined) {
      const href = safeHref(m[2]);
      if (href) out.push({ type: 'link', label: m[1], href });
      else pushText(m[0]);
      at = start + m[0].length;
    } else {
      const url = m[0].replace(TRAILING_PUNCT, '');
      const href = safeHref(url);
      if (href) out.push({ type: 'link', label: url, href });
      else pushText(url);
      at = start + url.length;
    }
  }
  pushText(text.slice(at));
  return out;
}

// ─── Rich text runs ─────────────────────────────────────────────────────────

export const richPlain = (r: RichText) => r.map((run) => run.text).join('');
export const richLength = (r: RichText) => richPlain(r).trim().length;

const bare = (u: string) => u.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');

/** What the portal's Copy button copies: plain text, with a link's address after its words unless they already are the address. */
export function richCopyText(r: RichText): string {
  return r
    .map((run) => {
      if (!run.href) return run.text;
      const address = run.href.startsWith('mailto:') ? run.href.slice(7) : run.href;
      return bare(run.text) === bare(address) ? run.text : `${run.text} (${address})`;
    })
    .join('');
}

const sameFormat = (a: RichRun, b: RichRun) =>
  !!a.bold === !!b.bold && !!a.italic === !!b.italic && a.href === b.href && a.size === b.size;

/** Join neighbouring runs that share formatting and drop empty ones. */
export function mergeRuns(runs: RichText): RichText {
  const out: RichText = [];
  for (const run of runs) {
    if (!run.text) continue;
    const last = out[out.length - 1];
    if (last && sameFormat(last, run)) last.text += run.text;
    else out.push({ ...run });
  }
  return out;
}

/** Strip leading and trailing whitespace (including blank lines) from the whole field. */
export function trimRuns(runs: RichText): RichText {
  const out = mergeRuns(runs);
  while (out.length && !out[0].text.trimStart()) out.shift();
  while (out.length && !out[out.length - 1].text.trimEnd()) out.pop();
  if (out.length) {
    out[0] = { ...out[0], text: out[0].text.trimStart() };
    out[out.length - 1] = { ...out[out.length - 1], text: out[out.length - 1].text.trimEnd() };
  }
  return out;
}

/**
 * Runs from a stored field: an array of runs, or an older plain string.
 * `links` says whether that string used the [label](url) syntax (subtitle and
 * section text did; titles, labels and prompts were plain).
 */
export function toRich(raw: unknown, links = true): RichText {
  if (typeof raw === 'string') {
    if (!links) return raw ? [{ text: raw }] : [];
    return tokenizeLinks(raw).map((t) => (t.type === 'text' ? { text: t.value } : { text: t.label, href: t.href }));
  }
  if (!Array.isArray(raw)) return [];
  const out: RichText = [];
  for (const r of raw) {
    const o = obj(r);
    if (!str(o.text)) continue;
    const run: RichRun = { text: str(o.text) };
    if (o.bold === true) run.bold = true;
    if (o.italic === true) run.italic = true;
    if (str(o.href)) run.href = str(o.href);
    if (typeof o.size === 'number' && Number.isFinite(o.size)) run.size = o.size;
    out.push(run);
  }
  return mergeRuns(out);
}

/** Turns what an admin typed into a link address: adds https:// to bare domains, mailto: to emails. */
export function normalizeLinkInput(raw: string): string {
  const v = raw.trim();
  if (!v) return v;
  if (/^(https?:|mailto:)/i.test(v)) return v;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return `mailto:${v}`;
  return `https://${v}`;
}

// ─── Editor <-> stored shape ────────────────────────────────────────────────

function editableImage(raw: unknown): GuideImage | null {
  const i = obj(raw);
  if (!str(i.url)) return null;
  return {
    url: str(i.url),
    alt: str(i.alt),
    position: i.position === 'bottom' ? 'bottom' : 'top',
    width: i.width === 'medium' || i.width === 'small' ? i.width : 'full',
  };
}

/**
 * Bring a stored guide into the editor's shape without dropping anything the
 * admin wrote (unlike the portal's normaliser, which also trims and filters).
 * Missing colours and sizes fall back to the defaults.
 */
export function toEditable(raw: unknown): CoachGuide {
  const g = obj(raw);
  if (!Array.isArray(g.sections)) return structuredClone(DEFAULT_GUIDE);
  const t = obj(g.theme);
  const theme = Object.fromEntries(
    (Object.keys(DEFAULT_THEME) as (keyof GuideTheme)[]).map((k) => [k, HEX_COLOR.test(str(t[k])) ? str(t[k]) : DEFAULT_THEME[k]]),
  ) as GuideTheme;
  const ty = obj(g.typography);
  const typography = Object.fromEntries(
    (Object.keys(DEFAULT_TYPOGRAPHY) as (keyof GuideTypography)[]).map((k) => [
      k,
      typeof ty[k] === 'number' && Number.isFinite(ty[k]) ? (ty[k] as number) : DEFAULT_TYPOGRAPHY[k],
    ]),
  ) as GuideTypography;
  return {
    version: 1,
    title: toRich(g.title, false),
    subtitle: toRich(g.subtitle),
    theme,
    typography,
    sections: g.sections.map((rs) => {
      const s = obj(rs);
      const kind: SectionKind = s.kind === 'text' ? 'text' : 'prompts';
      return {
        id: str(s.id) || newId(),
        kind,
        eyebrow: toRich(s.eyebrow, false),
        body: toRich(s.body),
        accent: HEX_COLOR.test(str(s.accent)) ? str(s.accent) : null,
        image: editableImage(s.image),
        items: Array.isArray(s.items)
          ? s.items.map((ri) => ({ id: str(obj(ri).id) || newId(), title: toRich(obj(ri).title, false), text: toRich(obj(ri).text, false) }))
          : [],
      };
    }),
  };
}

/** Trimmed copy for saving; text sections never carry prompts. */
export function toStored(g: CoachGuide): CoachGuide {
  return {
    version: 1,
    title: trimRuns(g.title),
    subtitle: trimRuns(g.subtitle),
    theme: { ...g.theme },
    typography: { ...g.typography },
    sections: g.sections.map((s) => ({
      id: s.id,
      kind: s.kind,
      eyebrow: trimRuns(s.eyebrow),
      body: trimRuns(s.body),
      accent: s.accent,
      image: s.image ? { ...s.image, alt: s.image.alt.trim() } : null,
      items: s.kind === 'prompts' ? s.items.map((p) => ({ id: p.id, title: trimRuns(p.title), text: trimRuns(p.text) })) : [],
    })),
  };
}

/**
 * Field errors keyed by path: 'title', 'theme.accent', 'typography.titleSize',
 * 'sections.<id>.eyebrow', 'sections.<id>.items', 'items.<id>.title', ...
 * An empty object means savable.
 */
export function validateGuide(g: CoachGuide): Record<string, string> {
  const e: Record<string, string> = {};
  const L = GUIDE_LIMITS;
  const tooLong = (v: string, max: number) => v.trim().length > max && `Keep it under ${max} characters`;
  /** Length, link and size problems in a rich field. */
  const richError = (r: RichText, max: number) => {
    if (richLength(r) > max) return `Keep it under ${max} characters`;
    for (const run of r) {
      if (run.href && !safeHref(run.href)) return `The link “${run.text.trim()}” needs a web address starting with https:// (or an email)`;
      if (run.size != null && (run.size < RICH_SIZE_RANGE.min || run.size > RICH_SIZE_RANGE.max)) {
        return `Text sizes must be ${RICH_SIZE_RANGE.min}–${RICH_SIZE_RANGE.max}px`;
      }
    }
    return null;
  };

  if (!richLength(g.title)) e.title = 'Add a page title';
  else if (richError(g.title, L.title)) e.title = richError(g.title, L.title) as string;
  const subtitleErr = richError(g.subtitle, L.subtitle);
  if (subtitleErr) e.subtitle = subtitleErr;

  for (const k of Object.keys(DEFAULT_THEME) as (keyof GuideTheme)[]) {
    if (!HEX_COLOR.test(g.theme[k])) e[`theme.${k}`] = 'Use a hex colour like #ff70c4';
  }

  for (const f of TYPOGRAPHY_FIELDS) {
    const v = g.typography[f.key];
    if (!Number.isFinite(v) || v < f.min || v > f.max) e[`typography.${f.key}`] = `Use a size from ${f.min} to ${f.max}px`;
  }

  if (!g.sections.length) e.sections = 'Add at least one section';
  if (g.sections.length > L.sections) e.sections = `At most ${L.sections} sections`;

  for (const s of g.sections) {
    const p = `sections.${s.id}`;
    const eyebrowErr = richError(s.eyebrow, L.eyebrow);
    if (eyebrowErr) e[`${p}.eyebrow`] = eyebrowErr;
    if (s.accent && !HEX_COLOR.test(s.accent)) e[`${p}.accent`] = 'Use a hex colour like #ff70c4';
    if (s.image) {
      if (!s.image.url.startsWith('https://')) e[`${p}.image`] = 'Upload the image again';
      else if (tooLong(s.image.alt, L.alt)) e[`${p}.image`] = `Keep the description under ${L.alt} characters`;
    }

    const bodyErr = richError(s.body, L.body);
    if (s.kind === 'text') {
      if (!richLength(s.body) && !s.image) e[`${p}.body`] = 'Add text or an image to this section';
      else if (bodyErr) e[`${p}.body`] = bodyErr;
      continue;
    }
    if (bodyErr) e[`${p}.body`] = bodyErr;
    if (!s.items.length) e[`${p}.items`] = 'Add at least one prompt';
    if (s.items.length > L.items) e[`${p}.items`] = `At most ${L.items} prompts per section`;
    for (const it of s.items) {
      if (!richLength(it.title)) e[`items.${it.id}.title`] = 'Add a title';
      else if (richError(it.title, L.promptTitle)) e[`items.${it.id}.title`] = richError(it.title, L.promptTitle) as string;
      if (!richLength(it.text)) e[`items.${it.id}.text`] = 'Add the prompt text';
      else if (richError(it.text, L.promptText)) e[`items.${it.id}.text`] = richError(it.text, L.promptText) as string;
    }
  }
  return e;
}

// ─── Saving ─────────────────────────────────────────────────────────────────

export class GuideConflictError extends Error {
  constructor() {
    super('Someone else saved the guide while you were editing. Reload to see their version, then make your changes again.');
    this.name = 'GuideConflictError';
  }
}

export async function fetchCoachGuide(): Promise<CoachGuideRow | null> {
  const { data, error } = await supabase
    .from('coach_help_guide')
    .select('content, revision, updated_at, updated_by_email')
    .eq('id', 'default')
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

/**
 * Save `content`, guarded by the revision the editor loaded (null = no row
 * yet). Throws GuideConflictError if someone else saved in between.
 */
export async function saveCoachGuide(content: CoachGuide, baseRevision: number | null): Promise<CoachGuideRow> {
  const { data: { session } } = await supabase.auth.getSession();
  const stamp = {
    content,
    updated_by: session?.user?.id ?? null,
    updated_by_email: session?.user?.email ?? null,
    updated_at: new Date().toISOString(),
  };
  const columns = 'content, revision, updated_at, updated_by_email';

  if (baseRevision == null) {
    const { data, error } = await supabase
      .from('coach_help_guide')
      .insert({ id: 'default', revision: 1, ...stamp })
      .select(columns)
      .single();
    if (error?.code === '23505') throw new GuideConflictError();
    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase
    .from('coach_help_guide')
    .update({ revision: baseRevision + 1, ...stamp })
    .eq('id', 'default')
    .eq('revision', baseRevision)
    .select(columns)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new GuideConflictError();
  return data;
}

// ─── Images ─────────────────────────────────────────────────────────────────

// Image upload is switched off in the editor, and this bucket has no migration yet:
// add one (public read, admin-only insert) before re-enabling it.
export const GUIDE_IMAGE_BUCKET = 'coach-guide-assets';
export const GUIDE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
export const GUIDE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** Upload an image for a guide section and return its public URL. Every upload gets a fresh name. */
export async function uploadGuideImage(file: File): Promise<string> {
  if (!GUIDE_IMAGE_TYPES.includes(file.type)) throw new Error('Use a PNG, JPG, WebP or GIF image.');
  if (file.size > GUIDE_IMAGE_MAX_BYTES) throw new Error('Images must be 5 MB or smaller.');
  const ext = file.type === 'image/jpeg' ? 'jpg' : file.type.split('/')[1];
  const month = new Date().toISOString().slice(0, 7);
  const path = `sections/${month}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage
    .from(GUIDE_IMAGE_BUCKET)
    .upload(path, file, { contentType: file.type, cacheControl: '31536000', upsert: false });
  if (error) throw error;
  return supabase.storage.from(GUIDE_IMAGE_BUCKET).getPublicUrl(path).data.publicUrl;
}
